import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BuildIcon from '@mui/icons-material/Build';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { DynamicMuiIcon } from '../../shared/components/DynamicMuiIcon';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  AiAssistantRole,
  AiAssistantRoleSkill,
  AiAssistantRoleSkillStatus,
  AiAssistantRoleTool,
} from '../../core/types/data-models';
import { AiAssistantRoleDialog } from './AiAssistantRoleDialog';
import { AiAssistantRoleToolDialog } from './AiAssistantRoleToolDialog';

// ─── Skill dialog ─────────────────────────────────────────────────────────────

interface SkillDialogProps {
  open: boolean;
  initial?: AiAssistantRoleSkill | null;
  assistantRoleKey: string;
  onClose: () => void;
}

function SkillDialog({ open, initial, assistantRoleKey, onClose }: SkillDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [order, setOrder] = useState('0');
  const [status, setStatus] = useState<AiAssistantRoleSkillStatus>('ACTIVE');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setContent(initial?.content ?? '');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setStatus(initial?.status ?? 'ACTIVE');
      setError(null);
    }
  }, [open, initial]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        assistantRoleKey,
        name: name.trim(),
        description: description.trim(),
        content: content.trim(),
        order: order !== '' ? Number(order) : null,
        status,
      };
      if (initial) return resources.aiAssistantRoleSkills.update(initial.id, payload);
      return resources.aiAssistantRoleSkills.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aiAssistantRoleSkills', assistantRoleKey] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Skill' : 'New Skill'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="md"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <TextField label="Description" required value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} />
          <TextField
            label="Content"
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={8}
            helperText="System-prompt fragment concatenated into this role's session prompt."
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Order"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              helperText="Display order within the role (lower = first)"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as AiAssistantRoleSkillStatus)}
              sx={{ flex: 1 }}
            >
              {(['ACTIVE', 'DISABLED'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AiAssistantRoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roleId = Number(id);
  const qc = useQueryClient();

  const { data: role, isLoading: loadingRole, error: roleError } = useQuery({
    queryKey: ['aiAssistantRole', roleId],
    queryFn: () => resources.aiAssistantRoles.get(roleId),
    enabled: !isNaN(roleId),
  });

  const { data: allRolesEnv } = useQuery({
    queryKey: ['aiAssistantRoles'],
    queryFn: () => resources.aiAssistantRoles.list({ page: 1, pageSize: 200, sort: 'order' }),
  });
  const allRoles: AiAssistantRole[] = allRolesEnv?.data ?? [];

  const { data: skillsEnv, isLoading: loadingSkills } = useQuery({
    queryKey: ['aiAssistantRoleSkills', role?.key],
    queryFn: () =>
      resources.aiAssistantRoleSkills.list({ page: 1, pageSize: 200, sort: 'order', filter: { assistantRoleKey: role!.key } }),
    enabled: !!role?.key,
  });
  // Unfiltered — combined client-side below with default (assistantRoleKey null)
  // tools, which apply to every role and can't be expressed as a single equality filter.
  const { data: toolsEnv, isLoading: loadingTools } = useQuery({
    queryKey: ['aiAssistantRoleTools', role?.key],
    queryFn: () => resources.aiAssistantRoleTools.list({ page: 1, pageSize: 200, sort: 'order' }),
    enabled: !!role?.key,
  });

  const skills: AiAssistantRoleSkill[] = (skillsEnv?.data ?? []).slice().sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  const tools: AiAssistantRoleTool[] = (toolsEnv?.data ?? [])
    .filter((t) => t.assistantRoleKey === role?.key || t.assistantRoleKey === null)
    .slice()
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [skillDialog, setSkillDialog] = useState<{ open: boolean; editing: AiAssistantRoleSkill | null }>({ open: false, editing: null });
  const [toolDialog, setToolDialog] = useState<{ open: boolean; editing: AiAssistantRoleTool | null }>({ open: false, editing: null });
  const [deleteSkillTarget, setDeleteSkillTarget] = useState<AiAssistantRoleSkill | null>(null);
  const [deleteToolTarget, setDeleteToolTarget] = useState<AiAssistantRoleTool | null>(null);

  const deleteSkill = useMutation({
    mutationFn: (s: AiAssistantRoleSkill) => resources.aiAssistantRoleSkills.remove(s.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aiAssistantRoleSkills', role?.key] });
      setDeleteSkillTarget(null);
    },
  });
  const deleteTool = useMutation({
    mutationFn: (t: AiAssistantRoleTool) => resources.aiAssistantRoleTools.remove(t.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aiAssistantRoleTools'] });
      setDeleteToolTarget(null);
    },
  });

  if (loadingRole) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (roleError || !role) {
    return <Alert severity="error">{roleError ? extractErrorMessage(roleError) : 'AI Assistant Role not found.'}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            {role.logoImage ? (
              <Box component="img" src={role.logoImage} alt={role.name} sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <DynamicMuiIcon iconName={role.avatarIcon} sx={{ color: role.color || undefined }} />
            )}
            <span>{role.name}</span>
            <Chip label={role.status} size="small" variant="outlined" color={role.status === 'Active' ? 'success' : 'default'} />
          </Stack>
        }
        description={role.description}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setRoleDialogOpen(true)}>
              Edit
            </Button>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/global-settings/ai-assistant-roles')}>
              Back
            </Button>
          </Stack>
        }
      />

      {/* Skills */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>Skills</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setSkillDialog({ open: true, editing: null })}>
          New Skill
        </Button>
      </Stack>
      {loadingSkills ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>
      ) : skills.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>No skills yet. Click "New Skill" to add one.</Alert>
      ) : (
        <Box sx={{ mb: 4 }}>
          {skills.map((s) => (
            <Box key={s.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1, px: 2, py: 1.25, '&:hover .row-actions': { opacity: 1 } }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Chip label={s.order ?? '—'} size="small" variant="outlined" sx={{ minWidth: 32, mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                  {s.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.description}</Typography>
                  )}
                </Box>
                <StatusChip value={s.status} />
                <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                  <Tooltip title="Edit skill">
                    <IconButton size="small" onClick={() => setSkillDialog({ open: true, editing: s })}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete skill">
                    <IconButton size="small" color="error" onClick={() => setDeleteSkillTarget(s)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Tools */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>Tools</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setToolDialog({ open: true, editing: null })}>
          New Tool
        </Button>
      </Stack>
      {loadingTools ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>
      ) : tools.length === 0 ? (
        <Alert severity="info">No tools yet. Click "New Tool" to add one.</Alert>
      ) : (
        <Box>
          {tools.map((t) => (
            <Box key={t.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1, px: 2, py: 1, '&:hover .row-actions': { opacity: 1 } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip label={t.order ?? '—'} size="small" variant="outlined" sx={{ minWidth: 32 }} />
                <BuildIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1, fontFamily: 'monospace' }}>{t.toolName}</Typography>
                {t.assistantRoleKey === null && (
                  <Chip label="Default — all roles" size="small" color="info" variant="outlined" />
                )}
                <StatusChip value={t.status} />
                <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                  <Tooltip title="Edit tool">
                    <IconButton size="small" onClick={() => setToolDialog({ open: true, editing: t })}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete tool">
                    <IconButton size="small" color="error" onClick={() => setDeleteToolTarget(t)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <AiAssistantRoleDialog
        open={roleDialogOpen}
        initial={role}
        existingRoles={allRoles}
        onClose={() => setRoleDialogOpen(false)}
      />
      <SkillDialog
        open={skillDialog.open}
        initial={skillDialog.editing}
        assistantRoleKey={role.key}
        onClose={() => setSkillDialog({ open: false, editing: null })}
      />
      <AiAssistantRoleToolDialog
        open={toolDialog.open}
        initial={toolDialog.editing}
        assistantRoleKey={role.key}
        onClose={() => setToolDialog({ open: false, editing: null })}
      />
      <ConfirmDialog
        open={deleteSkillTarget !== null}
        title="Delete Skill"
        message={`Are you sure you want to delete "${deleteSkillTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteSkill.isPending}
        onConfirm={() => deleteSkillTarget && deleteSkill.mutate(deleteSkillTarget)}
        onClose={() => setDeleteSkillTarget(null)}
      />
      <ConfirmDialog
        open={deleteToolTarget !== null}
        title="Delete Tool"
        message={`Are you sure you want to delete "${deleteToolTarget?.toolName}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteTool.isPending}
        onConfirm={() => deleteToolTarget && deleteTool.mutate(deleteToolTarget)}
        onClose={() => setDeleteToolTarget(null)}
      />
    </Box>
  );
}
